import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

const cfgEl = document.getElementById('cccp-gift-config');

if (cfgEl) {
  const cfg = JSON.parse(cfgEl.textContent);
  const root = window.Shopify?.routes?.root || '/';
  let busy = false;
  let dispatching = false;

  const post = (path, body) =>
    fetch(`${root}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((r) => r.json());

  const getCart = () => fetch(`${root}cart.js`).then((r) => r.json());
  // { line key: 'physical' | 'digital' | '' } from templates/cart.cccp-books.liquid
  const getBookTypes = () => fetch(`${root}cart?view=cccp-books`).then((r) => r.json()).catch(() => ({}));

  // refresh Horizon cart UI
  const announce = (cart, lines) => {
    const deferred = CartLinesUpdateEvent.createPromise();
    dispatching = true;
    try {
      document.dispatchEvent(
        new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines, promise: deferred.promise })
      );
    } finally {
      dispatching = false;
    }
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, itemCount: cart.item_count, source: 'cccp-gifts', didError: false },
    });
  };

  const cccpSyncGifts = async () => {
    if (busy) return;
    busy = true;
    try {
      const cart = await getCart();
      const isGift = (i) => i.properties && i.properties._gift;
      const paid = cart.items.filter((i) => !isGift(i));
      const gifts = cart.items.filter(isGift);

      // One complimentary book per order: printed if any tie comes with it, else the
      // e-book if any does, else none.
      const books = paid.length ? Object.values(await getBookTypes()) : [];
      const book = books.includes('physical') ? 'physical' : books.includes('digital') ? 'digital' : '';
      const wanted =
        paid.length === 0
          ? []
          : cfg.gifts.filter(
              (g) => g.variant && !(g.nonMemberOnly && cfg.isMember) && (!g.book || g.book === book)
            );
      const wantedIds = wanted.map((g) => String(g.variant));
      const changed = [];

      for (const g of gifts) {
        if (!wantedIds.includes(String(g.variant_id)) || g.quantity !== 1) {
          const quantity = wantedIds.includes(String(g.variant_id)) ? 1 : 0;
          await post('cart/change.js', { id: g.key, quantity });
          changed.push({ id: g.key, quantity });
        }
      }
      const present = gifts.map((g) => String(g.variant_id));
      const toAdd = wanted.filter((g) => !present.includes(String(g.variant)));
      if (toAdd.length) {
        await post('cart/add.js', {
          items: toAdd.map((g) => ({ id: g.variant, quantity: 1, properties: { _gift: 'true' } })),
        });
        toAdd.forEach((g) => changed.push({ id: String(g.variant), quantity: 1 }));
      }

      if (changed.length) announce(await getCart(), changed);
    } catch (err) {
      console.warn('[cccp] gift sync failed', err);
    } finally {
      busy = false;
    }
  };

  window.cccpSyncGifts = cccpSyncGifts;

  // The cart belongs to the browser, not the customer: after a logout, or when
  // someone else signs in, start from an empty cart. A guest who signs in keeps it.
  const switchedCustomer = async () => {
    const current = String(cfg.customerId || '');
    let previous = '';
    try {
      previous = localStorage.getItem('cccp-customer') || '';
      localStorage.setItem('cccp-customer', current);
    } catch {
      return;
    }
    if (previous && previous !== current) {
      const cart = await post('cart/clear.js', {});
      if (cart && cart.items) announce(cart, []);
    }
  };

  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    if (dispatching) return;
    event.promise?.then(cccpSyncGifts).catch(() => {});
  });

  const start = () => switchedCustomer().catch(() => {}).then(cccpSyncGifts);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
}
